"use client";

import { CheckCircleIcon, ExclamationCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";

interface TaskProgressModalProps {
    isOpen: boolean;
    onClose: () => void;
    status: "idle" | "processing" | "completed" | "failed";
    title?: string;
    description?: string;
    error?: string | null;
}

export function TaskProgressModal({
    isOpen,
    onClose,
    status,
    title = "Processing...",
    description,
    error
}: TaskProgressModalProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                    onClick={status === "processing" ? undefined : onClose}
                />

                {/* Modal Content */}
                <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 ring-1 ring-slate-900/5 transform transition-all">

                    {status === "processing" ? (
                        <div className="text-center py-4 animate-in fade-in zoom-in duration-300">
                            {/* Spinner */}
                            <div className="relative inline-flex mb-8">
                                <div className="w-24 h-24 border-4 border-slate-100 rounded-full" />
                                <div className="absolute inset-0 w-24 h-24 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin" />
                                <SparklesIcon className="absolute inset-0 m-auto h-10 w-10 text-indigo-600 animate-pulse" />
                            </div>

                            <h3 className="text-2xl font-bold text-slate-900 mb-2">
                                {title}
                            </h3>
                            <p className="text-slate-500 max-w-sm mx-auto mb-8">
                                {description || "Please wait while we process your request. This may take a few moments."}
                            </p>

                            {/* Indeterminate Progress Bar */}
                            <div className="max-w-xs mx-auto bg-slate-100 rounded-full h-2 overflow-hidden">
                                <div className="h-full bg-indigo-600 animate-[loading_1.5s_ease-in-out_infinite] w-1/2 rounded-full origin-left" />
                            </div>
                        </div>
                    ) : status === "completed" ? (
                        <div className="text-center py-4 animate-in fade-in zoom-in duration-300">
                            <div className="inline-flex items-center justify-center w-24 h-24 bg-emerald-100 text-emerald-600 rounded-full mb-8 ring-8 ring-emerald-50">
                                <CheckCircleIcon className="h-12 w-12" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">
                                Complete!
                            </h3>
                            <p className="text-slate-500 mb-8">
                                The task has been successfully scheduled.
                            </p>
                            <button
                                onClick={onClose}
                                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-200"
                            >
                                Done
                            </button>
                        </div>
                    ) : status === "failed" ? (
                        <div className="text-center py-4 animate-in fade-in zoom-in duration-300">
                            <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 text-red-600 rounded-full mb-8 ring-8 ring-red-50">
                                <ExclamationCircleIcon className="h-12 w-12" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">
                                Something went wrong
                            </h3>
                            <p className="text-slate-500 mb-8">
                                {error || "An unexpected error occurred."}
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-bold"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
